/**
 * Reaction Selector
 *
 * Picks an appropriate reaction from the bank based on context,
 * with session dedup to avoid repeating the same line.
 *
 * Energy thresholds (based on cumulativeDelta):
 *   0-1  = calm
 *   1-3  = engaged
 *   3-5  = excited
 *   5+   = peak
 */

import type {
  Reaction,
  ReactionContext,
  EnergyLevel,
} from './types';
import {
  ACCEPT_REACTIONS,
  CROSSING_REACTIONS,
  LOADING_REACTIONS,
  RESCORE_RESULT_REACTIONS,
  RESCORE_FAILURE_REACTIONS,
  COMPLETE_REACTIONS,
} from './reaction-banks';

/**
 * Determine energy level from cumulative score delta
 */
export function getEnergyFromDelta(cumulativeDelta: number): EnergyLevel {
  if (cumulativeDelta >= 5) return 'peak';
  if (cumulativeDelta >= 3) return 'excited';
  if (cumulativeDelta >= 1) return 'engaged';
  return 'calm';
}

/**
 * Get the candidate pool of reactions for a given context
 */
function getCandidatePool(ctx: ReactionContext): Reaction[] {
  const { trigger, tier } = ctx;

  switch (trigger) {
    case 'accept': {
      const severity = ctx.severity ?? 'medium';
      return ACCEPT_REACTIONS[tier]?.[severity] ?? [];
    }
    case 'crossing': {
      const target = ctx.crossingTarget ?? 'caution';
      return CROSSING_REACTIONS[tier]?.[target] ?? [];
    }
    case 'loading':
      return LOADING_REACTIONS[tier] ?? [];
    case 'rescore_result':
      return RESCORE_RESULT_REACTIONS[tier] ?? [];
    case 'rescore_failure':
      return RESCORE_FAILURE_REACTIONS[tier] ?? [];
    case 'complete':
      return COMPLETE_REACTIONS[tier] ?? [];
    default:
      return [];
  }
}

/** Cached RegExp patterns for template variable replacement */
const regexCache = new Map<string, RegExp>();

/**
 * Apply template variables to a reaction fallback string.
 * Replaces {key} tokens with values from templateVars.
 * Unknown variables are left as-is (no crashes).
 */
function applyTemplateVars(
  text: string,
  vars?: Record<string, string | number>,
): string {
  if (!vars) return text;
  let result = text;
  for (const [key, value] of Object.entries(vars)) {
    let pattern = regexCache.get(key);
    if (!pattern) {
      pattern = new RegExp(`\\{${key}\\}`, 'g');
      regexCache.set(key, pattern);
    }
    pattern.lastIndex = 0;
    result = result.replace(pattern, String(value));
  }
  return result;
}

/**
 * Select a reaction from the bank, excluding already-shown keys.
 *
 * Strategy:
 * 1. Get candidate pool for the trigger + tier + severity/target
 * 2. Filter out already-shown keys (session dedup)
 * 3. Prefer reactions matching the energy level from cumulativeDelta
 * 4. Fall back to any un-shown reaction if no energy match
 * 5. If all exhausted, reset and pick any (allows re-use after full cycle)
 *
 * @returns The selected reaction with template vars applied, or null if pool empty
 */
export function selectReaction(
  ctx: ReactionContext,
  shownKeys: Set<string>,
): { reaction: Reaction; text: string } | null {
  const pool = getCandidatePool(ctx);
  if (pool.length === 0) return null;

  const targetEnergy = getEnergyFromDelta(ctx.cumulativeDelta);

  // Filter out shown reactions
  const unseen = pool.filter((r) => !shownKeys.has(r.key));

  // Pick from unseen, preferring energy match
  const energyMatch = unseen.filter((r) => r.energy === targetEnergy);
  let candidates = energyMatch.length > 0 ? energyMatch : unseen;

  // If all exhausted, allow re-use (full cycle reset)
  if (candidates.length === 0) {
    const energyFallback = pool.filter((r) => r.energy === targetEnergy);
    candidates = energyFallback.length > 0 ? energyFallback : pool;
  }

  // Random pick from candidates
  const picked = candidates[Math.floor(Math.random() * candidates.length)];
  const text = applyTemplateVars(picked.fallback, ctx.templateVars);

  return { reaction: picked, text };
}
